import { useEffect, useMemo, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { jsPDF } from 'jspdf'
import autoTable from 'jspdf-autotable'
import { itemsService } from '../services/api'
import { useProductCardImage } from '../hooks/useProductCardImage'
import { useSignedMediaQueryRenewal } from '../hooks/useSignedMediaQueryRenewal'
import { itemsRefetchInterval, ITEMS_STALE_MS, shouldRetryItems } from '../utils/itemQueryPolicy'
import { itemsPriceQueryKey } from '../utils/priceRefreshPolicy'
import { getUserId } from '../utils/sessionUser'
import { useTranslation } from '../hooks/useTranslation'
import { usePrefersReducedMotion } from '../hooks/usePrefersReducedMotion'
import LiquidGlassButton from '../components/LiquidGlassButton'
import LanguageSelector from '../components/LanguageSelector'
import ThemeSwitcher from '../components/ThemeSwitcher'
import AppDock from '../components/AppDock'
import ComparisonLoader from '../components/ComparisonLoader'
import { useAuth } from '../contexts/AuthContext'
import { useCartsQuery } from '../hooks/useCartsQuery'
import { compareCarts } from '../utils/compareCartsPolicy'
import './CompareCartsPage.css'

const VIEW_OPTIONS = [
  { id: 'all', label: 'Tous', icon: '/Icon view list.png' },
  { id: 'common', label: 'Communs', icon: '/Icon compare.png' },
  { id: 'onlyA', label: 'Uniquement A', icon: '/Icon shopping cart.png' },
  { id: 'onlyB', label: 'Uniquement B', icon: '/Icon shopping cart.png' },
]

function formatPrice(value) {
  return new Intl.NumberFormat('fr-FR', {
    style: 'currency',
    currency: 'EUR',
  }).format(value ?? 0)
}

function itemName(item) {
  return item?.name?.trim() || 'Article sans nom'
}

function comparisonSentence(difference, nameA, nameB) {
  if (difference === 0) return `${nameA} et ${nameB} ont le même total.`
  if (difference > 0) return `${nameB} coûte ${formatPrice(difference)} de plus que ${nameA}.`
  return `${nameA} coûte ${formatPrice(Math.abs(difference))} de plus que ${nameB}.`
}

function Pictogram({ src, className = '' }) {
  return <img src={src} alt="" className={`compare-picto ${className}`.trim()} />
}

function ComparisonState({ title, message, actionLabel, onAction }) {
  return (
    <main className="compare-carts-page compare-state" role="status">
      <div className="compare-state-card">
        <h1>{title}</h1>
        <p>{message}</p>
        {onAction ? <LiquidGlassButton onClick={onAction}>{actionLabel}</LiquidGlassButton> : null}
      </div>
    </main>
  )
}

function ProductSide({ label, item, unitPrice, quantity, subtotal, priceChanged, quantityChanged }) {
  const { src, onError } = useProductCardImage(item)
  if (!item) {
    return (
      <div className="comparison-side is-absent">
        <span className="side-label">{label}</span>
        <p>Absent de ce panier</p>
      </div>
    )
  }

  return (
    <div className="comparison-side">
      <span className="side-label">{label}</span>
      <div className="product-heading">
        {src ? (
          <img
            src={src}
            alt=""
            className="comparison-image"
            onError={onError}
          />
        ) : (
          <div className="comparison-missing">
            <Pictogram src="/Icon photo size select actual.png" />
            <span>Image indisponible</span>
          </div>
        )}
        <h3>{itemName(item)}</h3>
      </div>
      <dl className="product-metrics">
        <div className={priceChanged ? 'is-diff' : undefined}>
          <dt>Prix unitaire</dt>
          <dd>{formatPrice(unitPrice)}</dd>
        </div>
        <div className={quantityChanged ? 'is-diff' : undefined}>
          <dt>Quantité</dt>
          <dd>{quantity}</dd>
        </div>
        <div>
          <dt>Sous-total</dt>
          <dd>{formatPrice(subtotal)}</dd>
        </div>
      </dl>
    </div>
  )
}

function relationCopy(row) {
  if (row.kind === 'onlyA') return { icon: '/Icon shopping cart.png', label: 'Seulement A' }
  if (row.kind === 'onlyB') return { icon: '/Icon shopping cart.png', label: 'Seulement B' }
  if (row.priceChanged && row.quantityChanged) {
    return { icon: '/Icon south.png', label: 'Prix et quantité' }
  }
  if (row.priceChanged) {
    return {
      icon: row.unitPriceB > row.unitPriceA ? '/Icon south.png' : '/Icon north.png',
      label: 'Prix différent',
    }
  }
  if (row.quantityChanged) {
    return {
      icon: '/Icon south.png',
      label: row.qtyGap?.type === 'unit-vs-refs' ? 'Unités et références' : 'Quantité différente',
    }
  }
  return { icon: '/Icon check.png', label: 'Égal' }
}

function ComparisonRow({ row }) {
  const relation = relationCopy(row)
  const changeLabels = []
  if (row.priceChanged) changeLabels.push('Prix différent')
  if (row.quantityChanged) changeLabels.push('Quantité différente')

  return (
    <article className={`comparison-row row-${row.kind}`}>
      <ProductSide
        label="Panier A"
        item={row.itemA}
        unitPrice={row.unitPriceA}
        quantity={row.quantityA}
        subtotal={row.subtotalA}
        priceChanged={row.priceChanged}
        quantityChanged={row.quantityChanged}
      />
      <div
        className="row-status"
        aria-label={changeLabels.join(', ') || (row.kind === 'common' ? 'Même référence' : 'Référence unique')}
      >
        <Pictogram src={relation.icon} />
        <span>{relation.label}</span>
      </div>
      <ProductSide
        label="Panier B"
        item={row.itemB}
        unitPrice={row.unitPriceB}
        quantity={row.quantityB}
        subtotal={row.subtotalB}
        priceChanged={row.priceChanged}
        quantityChanged={row.quantityChanged}
      />
    </article>
  )
}

function CompareCartsPage() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { t } = useTranslation()
  const { logout, user } = useAuth()
  const currentUserId = getUserId(user)
  const reducedMotion = usePrefersReducedMotion()
  const [activeView, setActiveView] = useState('all')
  const [allowImageFallback, setAllowImageFallback] = useState(false)
  const [filterPulse, setFilterPulse] = useState(false)

  const cartAId = searchParams.get('cart1')
  const cartBId = searchParams.get('cart2')
  const hasIds = Boolean(cartAId && cartBId)
  const isSameCart = hasIds && cartAId === cartBId
  const cartsQuery = useCartsQuery()
  const carts = cartsQuery.data ?? []
  const cartA = carts.find((cart) => String(cart.id) === String(cartAId))
  const cartB = carts.find((cart) => String(cart.id) === String(cartBId))
  const canLoadItems = hasIds && !isSameCart

  const itemsAKey = itemsPriceQueryKey(cartAId, currentUserId)
  const itemsBKey = itemsPriceQueryKey(cartBId, currentUserId)
  const itemsAQuery = useQuery({
    queryKey: itemsAKey,
    queryFn: () => itemsService.list(cartAId),
    enabled: canLoadItems,
    refetchOnWindowFocus: true,
    refetchOnMount: true,
    refetchOnReconnect: true,
    staleTime: ITEMS_STALE_MS,
    retry: shouldRetryItems,
    refetchInterval: itemsRefetchInterval,
    refetchIntervalInBackground: false,
  })
  const itemsBQuery = useQuery({
    queryKey: itemsBKey,
    queryFn: () => itemsService.list(cartBId),
    enabled: canLoadItems,
    refetchOnWindowFocus: true,
    refetchOnMount: true,
    refetchOnReconnect: true,
    staleTime: ITEMS_STALE_MS,
    retry: shouldRetryItems,
    refetchInterval: itemsRefetchInterval,
    refetchIntervalInBackground: false,
  })
  useSignedMediaQueryRenewal(itemsAKey, canLoadItems)
  useSignedMediaQueryRenewal(itemsBKey, canLoadItems)

  const itemsA = Array.isArray(itemsAQuery.data) ? itemsAQuery.data : []
  const itemsB = Array.isArray(itemsBQuery.data) ? itemsBQuery.data : []
  const comparison = useMemo(
    () => compareCarts(itemsA, itemsB, { allowExternalImageKeyFallback: allowImageFallback }),
    [itemsA, itemsB, allowImageFallback],
  )
  const rows = activeView === 'all' ? comparison.rows : comparison[activeView]
  const nameA = cartA?.name || 'Panier A'
  const nameB = cartB?.name || 'Panier B'
  const goToCarts = () => navigate('/carts', { state: { activeApp: 'carts' } })
  const totalDiff = comparison.totalDifference || 0
  const gapIcon = totalDiff === 0 ? '/Icon check.png' : totalDiff > 0 ? '/Icon south.png' : '/Icon north.png'

  useEffect(() => {
    if (!filterPulse) return undefined
    const timer = window.setTimeout(() => setFilterPulse(false), reducedMotion ? 0 : 420)
    return () => window.clearTimeout(timer)
  }, [filterPulse, reducedMotion])

  const exportComparisonReport = () => {
    const doc = new jsPDF()
    doc.setFontSize(18)
    doc.text('Rapport de comparaison de paniers', 14, 20)
    doc.setFontSize(11)
    doc.text(`A — ${nameA}: ${formatPrice(comparison.metricsA.total)} · ${comparison.metricsA.references} références · ${comparison.metricsA.units} unités`, 14, 34)
    doc.text(`B — ${nameB}: ${formatPrice(comparison.metricsB.total)} · ${comparison.metricsB.references} références · ${comparison.metricsB.units} unités`, 14, 42)
    doc.text(comparisonSentence(comparison.totalDifference, nameA, nameB), 14, 50)

    const body = comparison.rows.map((row) => [
      itemName(row.itemA),
      row.itemA ? `${formatPrice(row.unitPriceA)} × ${row.quantityA}` : 'Absent',
      row.itemA ? formatPrice(row.subtotalA) : '—',
      itemName(row.itemB),
      row.itemB ? `${formatPrice(row.unitPriceB)} × ${row.quantityB}` : 'Absent',
      row.itemB ? formatPrice(row.subtotalB) : '—',
      row.kind === 'common'
        ? [row.priceChanged ? 'Prix' : '', row.quantityChanged ? 'Quantité' : ''].filter(Boolean).join(' + ') || 'Égal'
        : row.kind === 'onlyA' ? 'A seulement' : 'B seulement',
    ])

    autoTable(doc, {
      startY: 60,
      head: [['Article A', 'PU × Qté A', 'Total A', 'Article B', 'PU × Qté B', 'Total B', 'Écart']],
      body,
      theme: 'grid',
      styles: { fontSize: 8, cellPadding: 2 },
      headStyles: { fillColor: [50, 55, 70] },
    })

    const safeName = (value) => value.replace(/[<>:"/\\|?*]+/g, '-').slice(0, 40)
    doc.save(`comparaison-${safeName(nameA)}-${safeName(nameB)}.pdf`)
  }

  if (!hasIds) {
    return <ComparisonState title="Comparaison impossible" message="Sélectionnez deux paniers à comparer." actionLabel="Choisir les paniers" onAction={goToCarts} />
  }
  if (isSameCart) {
    return <ComparisonState title="Deux paniers distincts requis" message="Un panier ne peut pas être comparé avec lui-même." actionLabel="Changer les paniers" onAction={goToCarts} />
  }
  if (cartsQuery.isLoading || itemsAQuery.isLoading || itemsBQuery.isLoading) {
    return <ComparisonLoader />
  }
  if (cartsQuery.isError || itemsAQuery.isError || itemsBQuery.isError) {
    return <ComparisonState title="Comparaison indisponible" message="Les données n’ont pas pu être chargées. Réessayez depuis la liste des paniers." actionLabel="Retour aux paniers" onAction={goToCarts} />
  }
  if (!cartA || !cartB) {
    return <ComparisonState title="Panier introuvable" message="L’un des paniers sélectionnés n’existe plus ou n’est pas accessible." actionLabel="Changer les paniers" onAction={goToCarts} />
  }

  return (
    <div className="compare-carts-page">
      <header className="compare-header">
        <div className="header-container">
          <div className="header-primary">
            <LiquidGlassButton onClick={goToCarts} className="btn-back-icon" aria-label={t('back')}>
              <img src="/Icon house.png" alt="" />
            </LiquidGlassButton>
            <ThemeSwitcher />
            <h1>{t('compareCarts')}</h1>
          </div>
          <div className="header-actions">
            <LiquidGlassButton onClick={exportComparisonReport} className="btn-export" title={t('exportReport')} aria-label={t('exportReport')}>
              <img src="/Icon download.png" alt="" />
            </LiquidGlassButton>
            <LanguageSelector position="right" />
            <LiquidGlassButton onClick={logout} className="btn-logout-icon" aria-label="Déconnexion">
              <img src="/Icon logout.png" alt="" />
            </LiquidGlassButton>
          </div>
        </div>
      </header>

      <main className="compare-content">
        <section className={`compare-summary${reducedMotion ? '' : ' is-animated'}`} aria-labelledby="comparison-summary-title">
          <div className="summary-heading">
            <p className="eyebrow">Comparaison</p>
            <button type="button" className="change-carts-link" onClick={goToCarts}>
              <Pictogram src="/Icon compare.png" />
              Changer les paniers
            </button>
          </div>
          <div className="summary-grid">
            <div className="summary-cart">
              <Pictogram src="/Icon shopping cart.png" />
              <h2 id="comparison-summary-title">{nameA}</h2>
              <strong key={comparison.metricsA.total} className="summary-total">{formatPrice(comparison.metricsA.total)}</strong>
              <span className="summary-meta">{comparison.metricsA.references} références · {comparison.metricsA.units} unités</span>
            </div>
            <div className="summary-cart">
              <Pictogram src="/Icon shopping cart.png" />
              <h2>{nameB}</h2>
              <strong key={comparison.metricsB.total} className="summary-total">{formatPrice(comparison.metricsB.total)}</strong>
              <span className="summary-meta">{comparison.metricsB.references} références · {comparison.metricsB.units} unités</span>
            </div>
          </div>
          <p className={`difference-sentence ${totalDiff === 0 ? 'is-even' : totalDiff > 0 ? 'is-higher' : 'is-lower'}`}>
            <Pictogram src={gapIcon} />
            <span>{comparisonSentence(comparison.totalDifference, nameA, nameB)}</span>
          </p>
        </section>

        <details className="compare-help">
          <summary>
            <Pictogram src="/Icon data exploration.png" />
            Comment les articles sont-ils comparés ?
          </summary>
          <div className="compare-help-body">
            <p>
              Deux articles sont associés lorsqu’ils pointent vers la même page produit, une fois le lien nettoyé des paramètres publicitaires.
              Le nom seul ne suffit jamais à les considérer comme identiques.
            </p>
            <label className="fallback-toggle">
              <input
                type="checkbox"
                checked={allowImageFallback}
                onChange={(event) => setAllowImageFallback(event.target.checked)}
              />
              <span>
                Associer aussi les articles qui partagent exactement la même image externe.
                Une image identique n’est pas une preuve suffisante qu’il s’agit du même produit.
              </span>
            </label>
            {comparison.collisions.length ? (
              <p className="collision-note" role="note">
                Certains articles n’ont pas pu être associés automatiquement. Ils restent affichés séparément.
              </p>
            ) : null}
          </div>
        </details>

        <nav className={`comparison-views${filterPulse ? ' is-pulsing' : ''}`} aria-label="Filtrer les références">
          {VIEW_OPTIONS.map((option) => {
            const count = option.id === 'all' ? comparison.rows.length : comparison[option.id].length
            return (
              <button
                type="button"
                key={option.id}
                className={activeView === option.id ? 'is-active' : ''}
                aria-pressed={activeView === option.id}
                onClick={() => {
                  if (option.id === activeView) return
                  setActiveView(option.id)
                  if (!reducedMotion) setFilterPulse(true)
                }}
              >
                <Pictogram src={option.icon} />
                {option.label}
                <span>{count}</span>
              </button>
            )
          })}
        </nav>

        {comparison.common.length === 0 ? (
          <div className="no-common" role="status">
            <strong>Aucun article équivalent</strong>
            <span>Les produits restent visibles dans « Uniquement A » et « Uniquement B ».</span>
          </div>
        ) : null}

        <section className={`comparison-list${filterPulse && !reducedMotion ? ' is-filtering' : ''}`} aria-live="polite">
          {rows.length ? rows.map((row, index) => (
            <div key={row.id} className="comparison-row-wrap" style={{ '--row-index': Math.min(index, 11) }}>
              <ComparisonRow row={row} />
            </div>
          )) : (
            <div className="empty-view">Aucun article dans cette vue.</div>
          )}
        </section>
      </main>

      <AppDock
        onAppSelect={(appId) => navigate('/carts', { state: { activeApp: appId } })}
        selectedApp={null}
      />
    </div>
  )
}

export default CompareCartsPage
